import { TransactionStatus } from '@rentguru/commons-utils';
import type { BankAccount, Transaction } from '@rentguru/commons-utils';
import type { QueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';

export const PAYMENTS_QUERY_ROOT = 'payments';
export const TREASURY_QUERY_ROOT = 'treasury';
export const FEE_ARREARS_QUERY_KEY = 'feeArrears';
export const BUS_ACTIVE_SAFETY_POLL_INTERVAL_IN_MS = 5 * 60 * 1000;

const PAYMENT_RELEVANT_BANK_ACCOUNT_FIELDS = [
  'balance',
  'buildingIds',
  'contactId',
  'contactIds',
  'disabled',
  'holderName',
  'ibanStatus',
  'ibanVerifiedNumber',
  'initiationChannel',
  'integrations',
  'label',
  'number',
  'paymentRunPriorityOrderIds',
  'retainedBalanceThreshold',
  'type',
] as const;

type PaymentRelevantBankAccount = Partial<Pick<BankAccount, (typeof PAYMENT_RELEVANT_BANK_ACCOUNT_FIELDS)[number]>>;

export const invalidatePayments = async (queryClient: QueryClient, clientId: string | null): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_ROOT, clientId], refetchType: 'active' }),
    queryClient.invalidateQueries({ queryKey: [TREASURY_QUERY_ROOT, clientId], refetchType: 'active' }),
  ]);
};

export const getPaymentsPollInterval = (isBusActive: boolean, intervalInMs: number): number =>
  isBusActive ? Math.max(intervalInMs, BUS_ACTIVE_SAFETY_POLL_INTERVAL_IN_MS) : intervalInMs;

export const hasPaymentRelevantBankAccountChange = (
  next: PaymentRelevantBankAccount,
  previous?: PaymentRelevantBankAccount
): boolean =>
  isNil(previous) ||
  !isEqual(pick(previous, PAYMENT_RELEVANT_BANK_ACCOUNT_FIELDS), pick(next, PAYMENT_RELEVANT_BANK_ACCOUNT_FIELDS));

export const isIncomingTransactionNewlyReconciled = (
  next: Pick<Transaction, 'amount' | 'status'>,
  previous?: Pick<Transaction, 'status'>
): boolean =>
  next.amount > 0 && next.status === TransactionStatus.RECONCILED && previous?.status !== TransactionStatus.RECONCILED;

export const invalidateFeeArrears = (queryClient: QueryClient, clientId: string | null): Promise<void> =>
  queryClient.invalidateQueries({
    queryKey: [TREASURY_QUERY_ROOT, clientId, FEE_ARREARS_QUERY_KEY],
    refetchType: 'all',
  });
